// Reports data layer — the FULL-FETCH snapshot (R-013) + all client-side
// filtering. Ports legacy fetchReportData / rptRange / rptApplyFilters onto the
// 2.0 data layer.
//
// ── R-013, the chair-P0 this phase exists to prove ─────────────────────────
// PostgREST hard-caps one request at 1,000 rows. v_20 fetched cases and cycles
// with a single .select() and then computed "total enrolled" off whatever came
// back, so past 1,000 patients every funder number was quietly wrong. Here EVERY
// stream goes through fetchAll()'s chunk loop, and the case set specifically
// comes from the SHARED loadAllCases() in data/cases.js — the single writer the
// Board and Registry already read. There is no second, capped read of the
// patient set anywhere in the app.
//
// ── ALL-OR-NOTHING (R-003 / the honest-metrics rule) ───────────────────────
// If ANY of the five queries fails, we render NOTHING and say so. A page that
// silently drops the cycles stream still shows a confident "Total enrolled" and
// a zeroed "Refills on time" — a report that is wrong in a way no reader can
// see. Legacy called this `_rptErr`; it is preserved exactly, extended to the
// expenses stream, and surfaced through the REPORTS slice's loadError.
//
// ── FETCH ONCE, SLICE MANY ─────────────────────────────────────────────────
// The period, clock, compare toggle and every slice chip are PURE functions over
// the snapshot. Changing a filter never touches the network — which is why the
// filter bar can be instant on a clinic connection, and why the compare window
// is computed from the same rows rather than a second round trip.

import { store, SLICES, flight } from '../state/store.js';
import { getClient, fetchAll, ok, fail } from '../db/client.js';
import { TABLES, VIEWS, STAGES, resolveStage, isClosedCase } from '../db/contract.js';
import { shaMeta, loadAllCases, allCases, casesLoaded } from './cases.js';
import { computeAll, metricIds, ageBand } from '../metrics/registry.js';

const reportsFlight = flight();

/** Test-only: reset the shared flight guard. @returns {void} */
export function _resetReports() {
  Object.assign(reportsFlight, flight());
}

// ─── Period windows ────────────────────────────────────────────────────────

export const PERIODS = Object.freeze([
  Object.freeze({ k: 'month', l: 'This month' }),
  Object.freeze({ k: 'quarter', l: 'This quarter' }),
  Object.freeze({ k: 'ytd', l: 'Year to date' }),
  Object.freeze({ k: '12m', l: 'Last 12 months' }),
  Object.freeze({ k: 'all', l: 'All time' }),
  Object.freeze({ k: 'custom', l: 'Custom…' }),
]);

// The CLOCK: which timestamp the period binds to. This is the question "in the
// last quarter" cannot answer on its own — in the last quarter by when they
// ENROLLED, or by when they last COLLECTED medicine? Getting this wrong is how
// a report says enrolment collapsed when in fact the clock changed.
export const CLOCKS = Object.freeze([
  Object.freeze({ k: 'enrol', l: 'Enrolment', d: 'The case was created in the period' }),
  Object.freeze({ k: 'refill', l: 'Refill', d: 'The patient had a medication cycle in the period' }),
  Object.freeze({ k: 'ae', l: 'Adverse event', d: 'The patient had an adverse event reported in the period' }),
  Object.freeze({ k: 'expense', l: 'Expense', d: 'Money was spent on the patient in the period' }),
]);

/**
 * Local midnight for a Date — the app's todayLocal equivalent, used so a period
 * boundary is the user's midnight in Nairobi, not UTC's. Building the Date from
 * (year, month, day) parts keeps it timezone-safe: no ISO string is ever parsed
 * back through a UTC assumption.
 * @param {Date} d
 * @returns {Date}
 */
export function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Parse a 'YYYY-MM-DD' input value as a LOCAL date (new Date('2026-01-31') is
 * parsed as UTC midnight by spec, which lands on the 30th in a negative-offset
 * zone — the classic off-by-one on custom ranges). Returns null when malformed.
 * @param {string|null|undefined} s
 * @returns {Date|null}
 */
export function parseLocalDate(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || '').trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const day = Number(m[3]);
  const d = new Date(y, mo - 1, day);
  if (isNaN(d.getTime())) return null;
  // Reject out-of-range parts rather than letting the Date constructor roll them
  // over: '2026-13-45' would otherwise become 14 February 2027, and a report
  // would silently cover a period nobody asked for.
  if (d.getFullYear() !== y || d.getMonth() !== mo - 1 || d.getDate() !== day) return null;
  return d;
}

/**
 * The period window {a, b} plus the PREVIOUS comparable window {pa, pb}, or null
 * for All time (which has no previous period to compare against).
 *
 * The previous window is the SAME LENGTH, ending exactly where this one starts:
 * a 31-day month compares against the 31 days before it, not against "last
 * calendar month" (which would be 28 days in February and make every February
 * look like a collapse). All boundaries are built from local date parts.
 *
 * @param {string} period one of PERIODS.k
 * @param {Date} [now]
 * @param {{from?: string, to?: string}} [custom] for period 'custom' (YYYY-MM-DD)
 * @returns {{a: Date, b: Date, pa: Date, pb: Date}|null}
 */
export function periodRange(period, now, custom) {
  const base = now instanceof Date && !isNaN(now.getTime()) ? now : new Date();
  /** @type {Date|null} */
  let a = null;
  let b = base;
  if (period === 'month') a = new Date(base.getFullYear(), base.getMonth(), 1);
  else if (period === 'quarter') a = new Date(base.getFullYear(), Math.floor(base.getMonth() / 3) * 3, 1);
  else if (period === 'ytd') a = new Date(base.getFullYear(), 0, 1);
  else if (period === '12m') a = new Date(base.getFullYear(), base.getMonth() - 11, 1);
  else if (period === 'custom') {
    const from = parseLocalDate(custom && custom.from);
    const to = parseLocalDate(custom && custom.to);
    if (!from) return null; // an incomplete custom range is All time, not a lie
    a = startOfDay(from);
    // The 'to' day is INCLUSIVE — a user picking 31 Jan means through the end of
    // the 31st, not up to its midnight (which would silently drop a day's rows).
    b = to ? new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 999) : base;
    if (b < a) return null;
  }
  if (!a) return null; // 'all'
  const span = b.getTime() - a.getTime();
  // The previous window is HALF-OPEN at its top: `inWindow` is inclusive at both
  // ends, so ending the previous window at `a` itself would let a row stamped
  // exactly at the period start satisfy BOTH windows and be counted twice —
  // inflating both sides of every Compare delta. Local midnight on the 1st is a
  // plausible timestamp for backfilled rows and for DATE-column streams, so this
  // is not theoretical. Backing off one millisecond keeps the two windows exactly
  // adjacent and non-overlapping without changing the span arithmetic.
  return { a, b, pa: new Date(a.getTime() - span), pb: new Date(a.getTime() - 1) };
}

/**
 * Is a timestamp inside a window? A missing timestamp is OUTSIDE every window —
 * a row with no date cannot be claimed for a period.
 * @param {any} t @param {{a: Date, b: Date}|null} win @returns {boolean}
 */
export function inWindow(t, win) {
  if (!win) return true; // All time
  if (!t) return false;
  const d = new Date(t).getTime();
  if (isNaN(d)) return false;
  return d >= win.a.getTime() && d <= win.b.getTime();
}

// ─── Slice dimensions (legacy RPT_SLICES + sex/age band) ───────────────────

// Age banding now lives in the metrics registry (R-022: ONE definition) because
// the equity tiles band ages too — two copies would drift the day someone moved
// a boundary. Re-exported here so the slice chips and every existing importer
// keep their import path.
export { ageBand };

// Every dimension the slice chips can filter on. `get` must be TOTAL — it always
// returns a display string, never null, so a menu never grows a blank row. '—'
// is a real, selectable bucket meaning "not recorded", which is exactly what a
// data-quality conversation needs.
export const SLICE_DIMS = Object.freeze([
  Object.freeze({ k: 'county', l: 'County', get: (c) => String(c.county || '').trim() || '—' }),
  Object.freeze({ k: 'facility', l: 'Facility', get: (c) => String(c.treating_facility || c.facility_name || '').trim() || '—' }),
  Object.freeze({ k: 'stage', l: 'Stage', get: (c) => (isClosedCase(c) ? 'Closed / Archived' : STAGES[resolveStage(c)].l) }),
  Object.freeze({ k: 'sha', l: 'SHA status', get: (c) => shaMeta(c.sha_status).label }),
  Object.freeze({ k: 'prescriber', l: 'Prescriber', get: (c) => String(c.physician_name || '').trim() || '—' }),
  Object.freeze({ k: 'nurse', l: 'Nurse', get: (c) => (c.owner && c.owner.full_name) || 'Unassigned' }),
  Object.freeze({ k: 'sex', l: 'Sex', get: (c) => {
    const s = String(c.sex || '').trim().toLowerCase();
    if (!s) return '—';
    return s.charAt(0).toUpperCase() + s.slice(1);
  } }),
  Object.freeze({ k: 'age', l: 'Age band', get: (c, now) => ageBand(c.dob || c.date_of_birth, now) }),
]);

/** A blank filter state (all dimensions empty). @returns {Record<string, string[]>} */
export function emptySlices() {
  /** @type {Record<string, string[]>} */
  const s = {};
  for (const d of SLICE_DIMS) s[d.k] = [];
  return s;
}

/** The default filter state on first visit. @returns {any} */
export function defaultFilters() {
  return { period: 'all', clock: 'enrol', compare: false, tier: 'board', custom: { from: '', to: '' }, slice: emptySlices() };
}

/**
 * Value → count for a slice dimension, over the WHOLE snapshot (not the filtered
 * set): the menu must keep showing a county you can still select even after you
 * have filtered it away, otherwise the filter becomes a one-way door.
 * @param {any} raw the snapshot
 * @param {string} dimKey
 * @param {Date} [now]
 * @returns {Array<{v: string, n: number}>}
 */
export function sliceValueCounts(raw, dimKey, now) {
  const dim = SLICE_DIMS.find((d) => d.k === dimKey);
  if (!dim) return [];
  /** @type {Record<string, number>} */
  const counts = {};
  for (const c of (raw && raw.cases) || []) {
    const v = dim.get(c, now);
    counts[v] = (counts[v] || 0) + 1;
  }
  return Object.keys(counts)
    .map((v) => ({ v, n: counts[v] }))
    .sort((a, b) => b.n - a.n || a.v.localeCompare(b.v));
}

/** True when any slice chip has a selection. @param {any} filters @returns {boolean} */
export function hasSliceSelection(filters) {
  const s = (filters && filters.slice) || {};
  return SLICE_DIMS.some((d) => (s[d.k] || []).length > 0);
}

// ─── The filter engine (pure) ──────────────────────────────────────────────

/**
 * Narrow the snapshot to the current period + slice selection. PURE — same input,
 * same output, no store reads, no network.
 *
 * Ordering matters and is ported exactly:
 *  1. slice chips narrow the CASES (they are all patient-dimension filters);
 *  2. the CLOCK then decides which of those cases fall in the period — by their
 *     own created_at (enrol), or by having a cycle / AE / expense in the window;
 *  3. the child streams (cycles, AEs, expenses) are narrowed to the surviving
 *     case ids ONCE any filter is active, and additionally to the window when the
 *     clock is theirs. Without step 3 a county filter would show that county's
 *     patients next to the whole programme's refill count.
 *  4. consents have NO case linkage — they can only be narrowed by the period
 *     (on the enrolment clock). Every consent tile says so in its caveat.
 *
 * @param {any} raw snapshot from fetchReportData
 * @param {any} filters {period, clock, compare, custom, slice}
 * @param {{now?: Date, window?: any}} [opts] pass `window` to override the period (compare pass)
 * @returns {{cases: any[], cycles: any[], consents: any[], aes: any[], aesAll: any[], expenses: any[], contacts: any[], stageHistory: any[], pd: any[], filtered: boolean, window: any}}
 */
export function applyFilters(raw, filters, opts) {
  const R = raw || {};
  const f = filters || defaultFilters();
  const now = (opts && opts.now) || new Date();
  const win =
    opts && Object.prototype.hasOwnProperty.call(opts, 'window')
      ? opts.window
      : periodRange(f.period, now, f.custom);
  const clock = f.clock || 'enrol';

  let cases = (R.cases || []).slice();

  // 1 — slice chips.
  const sel = f.slice || {};
  let sliced = false;
  for (const d of SLICE_DIMS) {
    const chosen = sel[d.k] || [];
    if (!chosen.length) continue;
    sliced = true;
    cases = cases.filter((c) => chosen.includes(d.get(c, now)));
  }

  // The case set as it stands BEFORE the clock narrows it to the period: it is
  // programme-scoped (loadAllCases applied the pill) and slice-scoped, but not
  // period-scoped. `aesAll` below is built from it.
  const preClockIds = new Set(cases.map((c) => c.case_id));

  // 2 — the clock decides period membership.
  if (win) {
    if (clock === 'refill') {
      const hit = new Set((R.cycles || []).filter((y) => inWindow(y && y.created_at, win)).map((y) => y.case_id));
      cases = cases.filter((c) => hit.has(c.case_id));
    } else if (clock === 'ae') {
      const hit = new Set((R.aes || []).filter((a) => inWindow(a && a.created_at, win)).map((a) => a.case_id));
      cases = cases.filter((c) => hit.has(c.case_id));
    } else if (clock === 'expense') {
      const hit = new Set(
        (R.expenses || []).filter((e) => inWindow((e && e.expense_date) || (e && e.created_at), win)).map((e) => e.case_id)
      );
      cases = cases.filter((c) => hit.has(c.case_id));
    } else {
      cases = cases.filter((c) => inWindow(c && c.created_at, win));
    }
  }

  const filtered = !!win || sliced;
  const ids = new Set(cases.map((c) => c.case_id));

  // 3 — child streams follow the surviving cases (and their own clock window).
  //
  // The case set is ALWAYS narrower than the child streams, even with no filter
  // on: loadAllCases applies the global programme pill, while the four supporting
  // streams are fetched programme-WIDE. So this narrowing is UNCONDITIONAL — it
  // used to be gated on `filtered`, which is false in the default All-time view,
  // and that paired one programme's patients with every programme's cycles, AEs
  // and expenses on the exact screen a board member opens and never touches.
  // Narrowing an unfiltered view loses nothing legitimate: `ids` is built from the
  // programme-scoped case set, so a dropped row is a row from another programme.
  const cycles = (R.cycles || []).filter((y) => {
    if (!ids.has(y && y.case_id)) return false;
    if (win && clock === 'refill') return inWindow(y && y.created_at, win);
    return true;
  });
  const aes = (R.aes || []).filter((a) => {
    if (!ids.has(a && a.case_id)) return false;
    if (win && clock === 'ae') return inWindow(a && a.created_at, win);
    return true;
  });
  const expenses = (R.expenses || []).filter((e) => {
    // Programme-level expenses carry no case_id. They belong to the programme,
    // not to a patient, so a patient-dimension SLICE must drop them (they cannot
    // be attributed) while an unsliced view keeps them. Stated as an explicit
    // case_id test rather than leaning on `filtered`, so the intent survives.
    if (e && e.case_id ? !ids.has(e.case_id) : sliced) return false;
    if (win && clock === 'expense') return inWindow((e && e.expense_date) || (e && e.created_at), win);
    return true;
  });

  // Contacts and stage history follow the surviving cases. Neither has a clock of
  // its own: outreach is read as "the contact history OF these patients", and the
  // stage snapshot is point-in-time by definition — narrowing either by the
  // period would silently answer a different question than the tile asks.
  const contacts = (R.contacts || []).filter((x) => ids.has(x && x.case_id));
  const stageHistory = (R.stageHistory || []).filter((x) => ids.has(x && x.case_id));

  // 4 — consents: period only, and only on the enrolment clock.
  let consents = (R.consents || []).slice();
  if (win && clock === 'enrol') consents = consents.filter((c) => inWindow(c && c.created_at, win));

  // `aesAll` is the programme-scoped AE stream BEFORE the period/clock narrowing.
  // serious_ae_mtd promises a current-calendar-month count "regardless of the
  // selected period" — that promise is what justifies its suppressed delta — so it
  // reads this rather than `aes`, which the clock would otherwise restrict to
  // patients falling in the period. A serious event reported this month on a
  // patient enrolled last year must not vanish from a pharmacovigilance headline.
  const aesAll = (R.aes || []).filter((a) => preClockIds.has(a && a.case_id));

  return { cases, cycles, consents, aes, aesAll, expenses, contacts, stageHistory, pd: R.pd || [], filtered, window: win || null };
}

/**
 * Compute every metric (or a named subset) over a filtered set — the ONE call
 * the screen, the CSV and the deck all make. Nothing computes a metric itself.
 * @param {any} filteredRows result of applyFilters
 * @param {{now?: Date, programmes?: any[], ids?: string[]}} [opts]
 * @returns {Record<string, any>}
 */
export function aggregate(filteredRows, opts) {
  const o = opts || {};
  return computeAll(
    filteredRows,
    { now: o.now || new Date(), window: filteredRows && filteredRows.window, programmes: o.programmes || [] },
    o.ids || metricIds()
  );
}

/**
 * The previous-period aggregate for the Compare toggle, or null when there is
 * nothing honest to compare against (All time has no previous period; Compare
 * off means no comparison was asked for).
 * @param {any} raw @param {any} filters @param {{now?: Date, programmes?: any[]}} [opts]
 * @returns {Record<string, any>|null}
 */
export function comparePrevious(raw, filters, opts) {
  const f = filters || defaultFilters();
  const now = (opts && opts.now) || new Date();
  if (!f.compare) return null;
  const win = periodRange(f.period, now, f.custom);
  if (!win) return null;
  const prevRows = applyFilters(raw, f, { now, window: { a: win.pa, b: win.pb } });
  return aggregate(prevRows, { now, programmes: (opts && opts.programmes) || [] });
}

// ─── The snapshot loader ───────────────────────────────────────────────────

// Columns pulled per stream. Named explicitly (not '*') for the streams where
// the row count is large and the report needs only a few columns — this is the
// difference between a 40 KB and a 4 MB payload on a clinic connection. Cases
// are the exception: they come from the shared loader, which selects '*' because
// the Board and Registry need the whole row.
// The clinical columns below were added when the reports layer grew its safety,
// adherence and treatment-journey tiles. They are all small scalars (dates,
// integers, short enums) — the payload cost is a few bytes per row, and each one
// retires a NEEDS FIELD card or powers a new one. Deliberately NOT selected:
// adverse_events.reaction_term / ae_description and medication_cycles.notes —
// free-text clinical narrative has no aggregate use and must not sit in a
// snapshot that feeds a CSV and a funder deck.
const CYCLE_COLS =
  'case_id,cycle_no,cycle_status,created_at,approved_on,expected_days_supply,next_refill_due_date,' +
  'treatment_start_confirmed_at,medication_name,ended_at,ended_reason,medication_change_reason';
const AE_COLS = 'case_id,cycle_no,seriousness,severity,outcome,status,created_at,onset_date,reported_to_pv_at';
const CONSENT_COLS = 'submission_id,status,created_at,programme_id';
const EXPENSE_COLS = 'expense_id,case_id,programme_id,category,amount,expense_date,created_at,is_billable';
// Contacts and stage history were the last two streams Reports did not read, and
// their absence was the ONLY reason three tiles were marked NEEDS FIELD. The app
// has been writing both all along (workspace.logContact stamps channel + outcome;
// every stage move appends a history row). Notes are excluded — free text again.
const CONTACT_COLS = 'case_id,channel,outcome,reached_party,contact_at,created_at';
const STAGE_HISTORY_COLS = 'case_id,to_stage,from_stage,created_at';

/**
 * Load the complete reporting snapshot. ALL-OR-NOTHING: the first stream error
 * aborts, writes loadError to the REPORTS slice and returns fail() — the view
 * renders its error state and NOT a single metric. A partially-loaded report is
 * indistinguishable from a true one on screen, which is why it is never rendered.
 *
 * Cases come from the SHARED loadAllCases() (data/cases.js) — the single writer
 * of SLICES.PATIENTS. Reports NEVER issues its own case query: a second, capped
 * read of the patient set is exactly the R-013 bug this phase fixes.
 *
 * @param {{client?: any, force?: boolean}} [deps]
 * @returns {Promise<any>}
 */
export function fetchReportData(deps) {
  return reportsFlight.share(() => _fetchInner(deps));
}

/**
 * @param {{client?: any, force?: boolean}} [deps]
 * @returns {Promise<any>}
 */
async function _fetchInner(deps) {
  const client = (deps && deps.client) || getClient();

  // 1 — the shared case set (chunk-looped past the 1,000-row cap, programme-filtered
  // by the global pill inside the loader).
  const caseRes = await loadAllCases({ client });
  if (caseRes && caseRes.stale) return { ok: false, data: null, error: null, code: 'STALE', stale: true };
  if (!caseRes || !caseRes.ok) {
    // `raw: null` is LOAD-BEARING, not tidiness. store.set MERGES object patches,
    // so omitting it leaves the previous snapshot readable — and every export path
    // reads the snapshot, not the error. That is how a failed refresh still built a
    // complete funder deck from stale rows and stamped it with a fresh generatedAt.
    store.set(SLICES.REPORTS, { raw: null, loaded: true, loadError: (caseRes && caseRes.error) || new Error('cases load failed') });
    return fail((caseRes && caseRes.error) || new Error('cases load failed'));
  }

  // 2 — the four supporting streams, each chunk-looped, in parallel.
  const [cycRes, aeRes, conRes, expRes, pdRes, ctcRes, shRes] = await Promise.all([
    fetchAll(() => client.from(TABLES.MEDICATION_CYCLES).select(CYCLE_COLS, { count: 'exact' }).order('created_at', { ascending: true })),
    fetchAll(() => client.from(TABLES.ADVERSE_EVENTS).select(AE_COLS, { count: 'exact' }).order('created_at', { ascending: true })),
    // Soft-deleted submissions excluded at the source, exactly as expenses are:
    // a removed record must not reach a funnel count or a deck slide.
    fetchAll(() =>
      client.from(TABLES.CONSENT_SUBMISSION).select(CONSENT_COLS, { count: 'exact' }).is('deleted_at', null).order('created_at', { ascending: true })
    ),
    // Soft-deleted expenses are excluded at the source — a deleted expense must
    // never reach a cost metric (it is the same `.is('deleted_at', null)` guard
    // the Billing view uses, so the two surfaces cannot disagree on the total).
    fetchAll(() =>
      client.from(TABLES.EXPENSES).select(EXPENSE_COLS, { count: 'exact' }).is('deleted_at', null).order('expense_date', { ascending: true })
    ),
    fetchAll(() => client.from(VIEWS.PROGRAMME_DASHBOARD).select('*', { count: 'exact' })),
    fetchAll(() => client.from(TABLES.CONTACTS).select(CONTACT_COLS, { count: 'exact' }).order('created_at', { ascending: true })),
    fetchAll(() => client.from(TABLES.CASE_STAGE_HISTORY).select(STAGE_HISTORY_COLS, { count: 'exact' }).order('created_at', { ascending: true })),
  ]);

  // ALL-OR-NOTHING. v_programme_dashboard is included deliberately: a failed read
  // there would otherwise render an honest-looking "No programme data yet."
  const err =
    (!cycRes.ok && cycRes.error) ||
    (!aeRes.ok && aeRes.error) ||
    (!conRes.ok && conRes.error) ||
    (!expRes.ok && expRes.error) ||
    (!pdRes.ok && pdRes.error) ||
    (!ctcRes.ok && ctcRes.error) ||
    (!shRes.ok && shRes.error) ||
    null;
  if (err) {
    console.warn('fetchReportData', err);
    // See the cases branch above: dropping `raw` is what makes ALL-OR-NOTHING
    // hold on the export paths, not just on the rendered screen.
    store.set(SLICES.REPORTS, { raw: null, loaded: true, loadError: err });
    return fail(err);
  }

  const raw = {
    cases: allCases(),
    cycles: cycRes.data || [],
    aes: aeRes.data || [],
    consents: conRes.data || [],
    expenses: expRes.data || [],
    pd: pdRes.data || [],
    contacts: ctcRes.data || [],
    stageHistory: shRes.data || [],
  };

  // TRUTHFUL CAP BANNER (R-013). fetchAll loops to exhaustion, so this should
  // never fire — but if a chunk loop ever exits early (an aborted page, a server
  // that stops honouring range), the page must SAY the numbers are partial rather
  // than present them as complete. Silence here is the bug we are fixing.
  const truncated = truncationReport(raw, {
    cases: typeof caseRes.total === 'number' ? caseRes.total : raw.cases.length,
    cycles: cycRes.total,
    aes: aeRes.total,
    consents: conRes.total,
    expenses: expRes.total,
    contacts: ctcRes.total,
    stageHistory: shRes.total,
  });

  store.set(SLICES.REPORTS, { raw, loaded: true, loadError: null, truncated });
  return Object.assign(ok(raw), { truncated });
}

/**
 * Compare fetched row counts against the server's exact totals. Returns null when
 * every stream is complete, or {streams:[{name, got, total}]} when any is short.
 * Pure — unit-tested without a network.
 * @param {any} raw
 * @param {Record<string, number|null|undefined>} totals
 * @returns {{streams: Array<{name: string, got: number, total: number}>}|null}
 */
export function truncationReport(raw, totals) {
  const short = [];
  const check = [
    ['cases', (raw && raw.cases) || []],
    ['cycles', (raw && raw.cycles) || []],
    ['aes', (raw && raw.aes) || []],
    ['consents', (raw && raw.consents) || []],
    ['expenses', (raw && raw.expenses) || []],
    ['contacts', (raw && raw.contacts) || []],
    ['stageHistory', (raw && raw.stageHistory) || []],
  ];
  for (const [name, rows] of check) {
    const total = totals && totals[/** @type {string} */ (name)];
    if (typeof total !== 'number') continue;
    if (rows.length < total) short.push({ name: String(name), got: rows.length, total });
  }
  return short.length ? { streams: short } : null;
}

// ─── Slice accessors ───────────────────────────────────────────────────────

/** The loaded snapshot, or null. @returns {any} */
export function reportsRaw() {
  return (store.get(SLICES.REPORTS) || {}).raw || null;
}

/** True once a report load has RESOLVED (success or failure) this session. @returns {boolean} */
export function reportsLoaded() {
  const s = store.get(SLICES.REPORTS) || {};
  // The case half of the snapshot lives in the PATIENTS slice; both must have
  // resolved before a view may paint an empty state instead of a skeleton.
  return !!s.loaded && casesLoaded();
}

/** The last load error, or null. @returns {any} */
export function reportsError() {
  return (store.get(SLICES.REPORTS) || {}).loadError || null;
}

/** The truncation report, or null when every stream was complete. @returns {any} */
export function reportsTruncated() {
  return (store.get(SLICES.REPORTS) || {}).truncated || null;
}
